/**
 * ComplianceHealthScore — single-number compliance pulse for the home page.
 *
 * Computes a weighted 0-100 across 8 axes: CAPA overdue / critical+major
 * deviations / OOS / training / audit findings / critical risks / restricted
 * equipment / suppliers at risk. Each axis has a max deduction so no single
 * issue collapses the score.
 */

import { useComplianceHealthScore } from '../../api/orgHooks';
import styles from './HomeDashboardCards.module.css';

const RADIUS = 64;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function bandColor(band: string): string {
  switch (band) {
    case 'green':
      return 'var(--color-status-success, #166534)';
    case 'amber':
      return 'var(--color-status-warning, #92400E)';
    case 'red':
      return 'var(--color-status-error, #B91C1C)';
    default:
      return 'var(--color-text-tertiary, #6B7989)';
  }
}

export function ComplianceHealthScore() {
  const q = useComplianceHealthScore();
  const d = q.data;
  if (q.isError) return null;

  const score = d?.score ?? 0;
  const band = d?.band ?? 'amber';
  const offset = CIRCUMFERENCE - (score / 100) * CIRCUMFERENCE;
  const color = bandColor(band);

  return (
    <section className={styles.scoreCard} aria-label="Compliance health score">
      <header className={styles.scoreHead}>
        <h2 className={styles.scoreTitle}>Compliance health</h2>
        <span className={styles.scoreSub}>Weighted across 8 quality axes</span>
      </header>

      <div className={styles.scoreBody}>
        <div className={styles.scoreDial}>
          <svg viewBox="0 0 160 160" className={styles.scoreSvg} aria-hidden>
            <circle
              cx="80"
              cy="80"
              r={RADIUS}
              fill="none"
              stroke="var(--neutral-100, #F3F4F6)"
              strokeWidth="14"
            />
            <circle
              cx="80"
              cy="80"
              r={RADIUS}
              fill="none"
              stroke={color}
              strokeWidth="14"
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={offset}
              transform="rotate(-90 80 80)"
              style={{ transition: 'stroke-dashoffset 600ms ease' }}
            />
          </svg>
          <div className={styles.scoreCenter}>
            <span className={styles.scoreNumber} style={{ color }}>
              {d?.score ?? '—'}
            </span>
            <span className={styles.scoreOf}>/100</span>
          </div>
        </div>

        <div className={styles.scoreMeta}>
          <span className={`${styles.scoreBand} ${styles[`band_${band}`]}`}>
            {d?.label ?? 'Loading…'}
          </span>
          <ul className={styles.scoreAxes}>
            {(d?.axes ?? []).map((a) => (
              <li key={a.axis} className={styles.scoreAxis}>
                <span className={styles.axisLabel}>{a.axis}</span>
                <div className={styles.axisBar}>
                  <div
                    className={styles.axisFill}
                    style={{
                      width: `${a.health}%`,
                      background:
                        a.health >= 85
                          ? 'var(--color-status-success, #166534)'
                          : a.health >= 60
                          ? 'var(--color-status-warning, #92400E)'
                          : 'var(--color-status-error, #B91C1C)',
                    }}
                  />
                </div>
                <span className={styles.axisHealth}>{a.health}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
